import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Customer, FixedTagCategory, ChatRoomFixedTag } from '../../../types';
import apiClient from '../../../utils/api';

interface CustomerProfileProps {
  customer: Customer | null;
  selectedChatId: string | null;
  onCustomerUpdate?: (updates: Partial<Customer>) => void;
}

export const CustomerProfile: React.FC<CustomerProfileProps> = ({
  customer,
  selectedChatId,
  onCustomerUpdate,
}) => {
  const [roomNotes, setRoomNotes] = useState('');
  const [projectNo, setProjectNo] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savingProjectNo, setSavingProjectNo] = useState(false);
  const [saveProjectNoSuccess, setSaveProjectNoSuccess] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [fixedTagCategories, setFixedTagCategories] = useState<FixedTagCategory[]>([]);
  const [roomFixedTags, setRoomFixedTags] = useState<Record<string, string>>({});
  const [savingFixedTags, setSavingFixedTags] = useState(false);

  // 過去に使用したタグ一覧 + 固定タグカテゴリを取得
  useEffect(() => {
    let isMounted = true;
    const loadInitData = async () => {
      try {
        const [tagRes, fixedRes] = await Promise.all([
          apiClient.getTagStats(),
          apiClient.getFixedTagCategories(),
        ]);
        if (isMounted) {
          if (tagRes.success && tagRes.tags) {
            setAvailableTags(tagRes.tags.map((tag: any) => tag.name));
          }
          if (fixedRes.success && fixedRes.categories) {
            setFixedTagCategories(fixedRes.categories);
          }
        }
      } catch (error: any) {
        if (!error.response || error.response.status !== 401) {
          console.error('タグ初期データ読み込みエラー:', error);
        }
      }
    };
    loadInitData();
    return () => { isMounted = false; };
  }, []);

  // チャットルームメモ・タグ・案件Noを読み込み
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const loadData = async () => {
      if (!selectedChatId) {
        setRoomNotes('');
        setProjectNo('');
        setCustomTags([]);
        setRoomFixedTags({});
        return;
      }
      try {
        console.log('📋 CustomerProfile: データ読み込み開始', selectedChatId);
        const [response, fixedTagRes] = await Promise.all([
          apiClient.getChatRoom(selectedChatId),
          apiClient.getChatRoomFixedTags(selectedChatId),
        ]);
        const chatRoom = response.data || response.chatRoom;
        if (isMounted && response.success && chatRoom) {
          setRoomNotes(chatRoom.notes || '');
          setProjectNo(chatRoom.project_no || '');
          setCustomTags(chatRoom.custom_tags || []);
        }
        if (isMounted && fixedTagRes.success && fixedTagRes.tags) {
          const tagMap: Record<string, string> = {};
          fixedTagRes.tags.forEach((t: ChatRoomFixedTag) => {
            tagMap[t.category_id] = t.option_id;
          });
          setRoomFixedTags(tagMap);
        }
        if (isMounted) console.log('✅ CustomerProfile: データ読み込み完了');
      } catch (error: any) {
        if (isMounted && (!error.response || error.response.status !== 401)) {
          console.error('❌ CustomerProfile: データ読み込みエラー:', error);
        }
      }
    };
    
    // 100ms後に実行（デバウンス）
    timeoutId = setTimeout(loadData, 100);
    
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedChatId]);

  // メモ保存ハンドラー
  const handleSaveNotes = async () => {
    if (!selectedChatId) return;
    
    setSavingNotes(true);
    setSaveSuccess(false);
    try {
      const response = await apiClient.updateChatRoomNotes(selectedChatId, roomNotes);
      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (error) {
      console.error('メモ保存エラー:', error);
      alert('メモの保存に失敗しました');
    } finally {
      setSavingNotes(false);
    }
  };

  // 案件No保存ハンドラー
  const handleSaveProjectNo = async () => {
    if (!selectedChatId) return;
    
    setSavingProjectNo(true);
    setSaveProjectNoSuccess(false);
    try {
      const response = await apiClient.updateChatRoomProjectNo(selectedChatId, projectNo);
      if (response.success) {
        setSaveProjectNoSuccess(true);
        setTimeout(() => setSaveProjectNoSuccess(false), 2000);
      }
    } catch (error) {
      console.error('案件No保存エラー:', error);
      alert('案件Noの保存に失敗しました');
    } finally {
      setSavingProjectNo(false);
    }
  };

  // タグ追加ハンドラー
  const handleAddTag = async () => {
    if (!newTag.trim() || !selectedChatId) return;
    
    try {
      const updatedTags = [...customTags, newTag.trim()];
      const response = await apiClient.updateChatRoomTags(selectedChatId, updatedTags);
      if (response.success) {
        setCustomTags(updatedTags);
        setNewTag('');
        console.log('✅ タグ追加成功');
      }
    } catch (error) {
      console.error('タグ追加エラー:', error);
      alert('タグの追加に失敗しました');
    }
  };

  // タグ削除ハンドラー
  const handleDeleteTag = async (tagToDelete: string) => {
    if (!selectedChatId) return;
    
    try {
      const updatedTags = customTags.filter(tag => tag !== tagToDelete);
      const response = await apiClient.updateChatRoomTags(selectedChatId, updatedTags);
      if (response.success) {
        setCustomTags(updatedTags);
        console.log('✅ タグ削除成功');
      }
    } catch (error) {
      console.error('タグ削除エラー:', error);
      alert('タグの削除に失敗しました');
    }
  };

  // 固定タグ変更ハンドラー
  const handleFixedTagChange = async (categoryId: string, optionId: string) => {
    if (!selectedChatId) return;

    const updated = { ...roomFixedTags };
    if (optionId) {
      updated[categoryId] = optionId;
    } else {
      delete updated[categoryId];
    }
    setRoomFixedTags(updated);

    setSavingFixedTags(true);
    try {
      const tags = Object.entries(updated).map(([catId, optId]) => ({
        category_id: catId,
        option_id: optId,
      }));
      await apiClient.updateChatRoomFixedTags(selectedChatId, tags);
      console.log('✅ 固定タグ更新成功');
    } catch (error) {
      console.error('固定タグ更新エラー:', error);
    } finally {
      setSavingFixedTags(false);
    }
  };

  // 会員レベルの表示
  const getMembershipLevelLabel = (level: string) => {
    switch (level) {
      case 'vip': return 'VIP';
      case 'premium': return 'プレミアム';
      case 'regular': return '一般';
      default: return level;
    }
  };

  const getMembershipLevelColor = (level: string) => {
    switch (level) {
      case 'vip': return 'error' as const;
      case 'premium': return 'warning' as const;
      case 'regular': return 'primary' as const;
      default: return 'default' as const;
    }
  };

  // 最終アクセス時間の表示
  const formatLastSeen = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 5) return 'オンライン';
    if (diffMins < 60) return `${diffMins}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    return `${diffDays}日前`;
  };

  if (!customer) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          <PersonIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body2">
            チャットを選択すると<br />顧客情報が表示されます
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* 顧客プロフィール */}
      <Card elevation={0} sx={{ borderRadius: 0 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                mr: 2,
                backgroundColor: 'primary.main',
                fontSize: '1.5rem',
              }}
            >
              {customer.name?.charAt(0) || 'C'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {customer.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={getMembershipLevelLabel(customer.membershipLevel)}
                  color={getMembershipLevelColor(customer.membershipLevel)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  {formatLastSeen(customer.lastSeen)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* 連絡先情報 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              連絡先情報
            </Typography>
            
            {/* 電話番号 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">{customer.phone}</Typography>
            </Box>

            {/* メールアドレス */}
            {customer.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{customer.email}</Typography>
              </Box>
            )}

            {/* 地域 */}
            {customer.city && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{customer.city}</Typography>
              </Box>
            )}
          </Box>

          {/* 固定タグ（商材・対応部署） */}
          {fixedTagCategories.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {fixedTagCategories.map((category) => (
                <Box key={category.id} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {category.name}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={roomFixedTags[category.id] || ''}
                      onChange={(e) => handleFixedTagChange(category.id, e.target.value as string)}
                      displayEmpty
                      disabled={!selectedChatId || savingFixedTags}
                      sx={{ fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="">
                        <em>未選択</em>
                      </MenuItem>
                      {category.options?.map((option) => (
                        <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.8125rem' }}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          )}

          {/* お問い合わせタグ */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              お問い合わせタグ
            </Typography>
            
            {/* タグ一覧 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {customTags && customTags.length > 0 ? (
                customTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onDelete={selectedChatId ? () => handleDeleteTag(tag) : undefined}
                    deleteIcon={<CloseIcon />}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  タグはありません
                </Typography>
              )}
            </Box>

            {/* タグ追加フィールド（オートコンプリート） */}
            {selectedChatId && (
              <Autocomplete
                freeSolo
                options={availableTags}
                value={newTag}
                onInputChange={(event, newValue) => {
                  setNewTag(newValue);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'string') {
                    setNewTag(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="タグを選択または入力..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={handleAddTag}
                              disabled={!newTag.trim()}
                              edge="end"
                            >
                              <AddIcon />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Box>

          {/* 案件No */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              案件No
            </Typography>
            <TextField
              fullWidth
              value={projectNo}
              onChange={(e) => setProjectNo(e.target.value)}
              placeholder="案件番号を入力..."
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth
              variant="contained"
              startIcon={savingProjectNo ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveProjectNo}
              disabled={savingProjectNo || !selectedChatId}
              color={saveProjectNoSuccess ? 'success' : 'primary'}
            >
              {saveProjectNoSuccess ? '保存しました！' : '案件Noを保存'}
            </Button>
          </Box>

          {/* チャットルームメモ */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              チャットルームメモ
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={roomNotes}
              onChange={(e) => setRoomNotes(e.target.value)}
              placeholder="このチャットルームに関するメモを入力..."
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth
              variant="contained"
              startIcon={savingNotes ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveNotes}
              disabled={savingNotes || !selectedChatId}
              color={saveSuccess ? 'success' : 'primary'}
            >
              {saveSuccess ? '保存しました！' : 'メモを保存'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
